import { motion } from 'framer-motion'
import { Rocket, Sparkles, BookOpen } from 'lucide-react'

interface AgeSelectorProps {
  onSelect: (age: '3' | '4' | '5') => void
}

const AGE_OPTIONS = [
  { 
    age: '3' as const, 
    emoji: '🧸', 
    title: "I'm 3!",
    subtitle: 'Counting & shapes',
    color: 'from-[#FFE66D] to-[#FFF5CC]',
    accent: '#F5A623'
  },
  { 
    age: '4' as const, 
    emoji: '🚀', 
    title: "I'm 4!",
    subtitle: 'Numbers & patterns',
    color: 'from-[#4ECDC4] to-[#E0F7F5]',
    accent: '#4ECDC4'
  },
  { 
    age: '5' as const, 
    emoji: '🎓', 
    title: "I'm 5!",
    subtitle: 'Addition & logic',
    color: 'from-[#9B5DE5] to-[#F0E6FF]',
    accent: '#9B5DE5'
  },
]

export default function AgeSelector({ onSelect }: AgeSelectorProps) {
  return (
    <div className="h-full flex flex-col items-center justify-center px-6 safe-top safe-bottom">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-10"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", delay: 0.2 }}
          className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[var(--math-primary)] to-[#FF8C42] rounded-3xl shadow-lg mb-4"
        >
          <BookOpen className="w-10 h-10 text-white" />
        </motion.div>
        <h1 className="display text-gray-800 mb-2">
          Math Adventures
        </h1>
        <p className="body-lg text-gray-500">
          Pick your age to begin!
        </p>
      </motion.div>

      {/* Age Options */}
      <div className="grid grid-cols-1 gap-4 w-full max-w-xs">
        {AGE_OPTIONS.map((option, index) => (
          <motion.button
            key={option.age}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 + index * 0.1, type: "spring" }}
            whileHover={{ scale: 1.03, y: -4 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => onSelect(option.age)}
            className={`
              relative overflow-hidden
              bg-gradient-to-r ${option.color}
              rounded-3xl p-6
              text-left
              shadow-md hover:shadow-xl
              transition-shadow duration-300
            `}
          >
            <div className="flex items-center gap-4">
              {/* Emoji */}
              <motion.span 
                className="text-4xl"
                animate={{ rotate: [0, -5, 5, 0] }}
                transition={{ repeat: Infinity, duration: 3, delay: index * 0.5 }}
              >
                {option.emoji}
              </motion.span>
              
              {/* Text */}
              <div>
                <span className="title block text-gray-800">{option.title}</span>
                <span className="text-sm text-gray-500 font-medium">{option.subtitle}</span>
              </div>

              {/* Arrow */}
              <motion.div 
                className="ml-auto"
                animate={{ x: [0, 4, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
              >
                <Sparkles className="w-5 h-5" style={{ color: option.accent }} />
              </motion.div>
            </div>

            {/* Decorative circle */}
            <div 
              className="absolute -right-4 -bottom-4 w-24 h-24 rounded-full opacity-20"
              style={{ backgroundColor: option.accent }}
            />
          </motion.button>
        ))}
      </div>

      {/* Footer note */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="mt-8 text-sm text-gray-400 flex items-center gap-2"
      >
        <Rocket className="w-4 h-4" />
        Learning made fun!
      </motion.p>
    </div>
  )
}
